import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import SectionTitle from './shared/SectionTitle';

const BoatsContainer = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(1.5),
    padding: theme.spacing(5, 3),
    textAlign: 'center',
    [theme.breakpoints.down('md')]: {
        padding: theme.spacing(4, 2),
    },
}));

const BoatBrandsList = styled(Typography)(({ theme }) => ({
    fontSize: '1.063rem',
    lineHeight: 1.8,
    color: theme.palette.text.primary,
    maxWidth: '900px',
    margin: '0 auto',
    '& .brand-item': {
        display: 'inline-block',
        whiteSpace: 'nowrap', // Prevent brand names from breaking mid-word
    },
    '& .separator': {
        color: theme.palette.primary.main,
        fontWeight: 600,
        margin: '0 8px',
        display: 'inline-block',
        whiteSpace: 'nowrap',
    },
    [theme.breakpoints.down('sm')]: {
        fontSize: '0.938rem', // Smaller font on mobile
        lineHeight: 1.6,
        maxWidth: '100%', // Allow full width on mobile
        padding: '0 8px', // Add padding to prevent edge touching
    },
}));

const ThumbnailGrid = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(6, 60px)', // Force 2 rows of 6
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(3),
    justifyContent: 'center',
    maxWidth: '800px',
    margin: '0 auto 24px auto',
    [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: 'repeat(4, 60px)', // 3 rows of 4 on mobile
    },
}));

const BoatThumbnail = styled('img')(({ theme }) => ({
    width: '60px',
    height: '80px',
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.grey[200],
    objectFit: 'cover',
    objectPosition: 'center',
    border: '2px solid',
    borderColor: theme.palette.grey[300],
    transition: 'transform 0.2s, border-color 0.2s',
    '&:hover': {
        transform: 'scale(1.05)',
        borderColor: theme.palette.orange?.main || '#e67e22',
    },
}));

const boatBrands = [
    'Beneteau',
    'Catalina',
    'C&C',
    'Fountaine Pajot',
    'Hallberg-Rassy',
    // Line break after Hallberg-Rassy
    'Hanse',
    'Hunter',
    'Jeanneau',
    'Lagoon',
    'Oyster',
    'Swan',
    'West Wight Potter'
];

const BoatsSupportedSection: React.FC = () => {
    const navigate = useNavigate();

    const handleFindBoat = () => {
        navigate('/quoter');
    };

    return (
        <BoatsContainer>
            {/* Boat thumbnails - actual customer boat images */}
            <ThumbnailGrid>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((num) => (
                    <BoatThumbnail
                        key={num}
                        src={`/images/thumbs/BoatModel${num}.jpg`}
                        alt={`Sailboat with Point Sails - Example ${num}`}
                        loading="lazy"
                    />
                ))}
            </ThumbnailGrid>

            <SectionTitle 
                centered 
                sx={{ 
                    mb: 2,
                    fontSize: { xs: '1.5rem', sm: '1.75rem' }
                }}
            >
                Supporting Over 5,000 Boat Models
            </SectionTitle>

            <Typography 
                variant="body1" 
                align="center"
                sx={{ 
                    mb: 3,
                    fontSize: { xs: '1rem', sm: '1.063rem' },
                    color: 'text.secondary',
                    maxWidth: '700px',
                    margin: '0 auto 24px auto'
                }}
            >
                We've designed precision-fit sails for thousands of production boats, including popular models from:
            </Typography>

            <BoatBrandsList>
                {boatBrands.map((brand, index) => (
                    <React.Fragment key={brand}>
                        <span className="brand-item">{brand}</span>
                        {/* Hard return after Hallberg-Rassy */}
                        {index === 4 ? (
                            <br />
                        ) : index < boatBrands.length - 1 ? (
                            <span className="separator">•</span>
                        ) : null}
                    </React.Fragment>
                ))}
            </BoatBrandsList>

            <Box sx={{ mt: 4 }}>
                <Button
                    variant="contained"
                    size="large"
                    onClick={handleFindBoat}
                    sx={{
                        fontSize: '1rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        px: 4,
                    }}
                >
                    Find Your Boat
                </Button>
            </Box>
        </BoatsContainer>
    );
};

export default BoatsSupportedSection;
